import React, { useEffect, useState } from 'react';
import { Shell } from '../components/Shell';
import { AuditLogEntry, AuthError, listAuditLog } from '../lib/auth';

/**
 * Super-admin view of recent platform-level actions: enterprise create /
 * suspend / delete / rename, etc. Read-only; the entries themselves are
 * written by route handlers via logAudit().
 */
export function SuperAdminAuditLog() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(200);

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        setEntries(await listAuditLog(limit));
      } catch (e) {
        setError(e instanceof AuthError ? e.message : 'Failed to load audit log.');
      }
    })();
  }, [limit]);

  return (
    <Shell active="audit">
      <div className="oa-root">
      <main className="oa-main">
        <h1 className="oa-title">Audit log</h1>
        <p className="oa-sub">Recent platform-level actions. Append-only.</p>

        {error && <div className="oa-msg oa-msg--err" style={{ margin: '14px 0' }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '12px 0' }}>
          <span className="oa-cell-muted">Show last</span>
          <select className="oa-select" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
            <option value={50}>50</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
          <span className="oa-cell-muted">entries</span>
        </div>

        <div className="oa-card" style={{ marginTop: 8, overflow: 'hidden' }}>
          <div className="oa-table-head">
            {['When', 'Action', 'Target', 'Actor', 'Details'].map((h, i) => (
              <span key={i} className="oa-th">{h}</span>
            ))}
          </div>
          {entries.map((e) => (
            <div key={e.id} className="oa-row">
              <span className="oa-cell-muted" style={{ fontSize: 12 }}>
                {new Date(e.createdAt).toLocaleString()}
              </span>
              <span><span className="oa-pill">{e.action}</span></span>
              <span className="oa-cell-muted" style={{ fontSize: 12 }}>
                {e.targetType ? `${e.targetType}: ` : ''}{e.targetId ?? '—'}
              </span>
              <span className="oa-cell-muted" style={{ fontSize: 12 }}>{e.actorUserId ?? 'system'}</span>
              <span className="oa-cell-muted" style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {e.payload ? JSON.stringify(e.payload) : ''}
              </span>
            </div>
          ))}
          {entries.length === 0 && !error && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--oa-muted)' }}>No audit entries yet.</div>
          )}
        </div>
      </main>
      </div>
    </Shell>
  );
}
